import { HUBS } from '../hubs.js';
import { NAV_MAP } from '../navigation.js';
import { SETTINGS_SECTIONS } from '../settings/settingsNav.js';

/**
 * What the browser tab says, and what a screen reader reads out first when a
 * route changes.
 *
 * `index.html` sets `<title>phibian — Admin</title>` once and nothing ever
 * changed it, so every screen in the product had the same name. That is not a
 * cosmetic gap: the document title is how a screen-reader user knows a
 * navigation happened and where it landed, and it is what the browser puts in
 * history and in the tab strip when someone has four screens open.
 *
 * Titles are DERIVED from the navigation maps that already exist rather than
 * kept in a second table that would drift out of step with the first. A route
 * with no nav entry falls back to the app name alone — honest, and visibly
 * missing if someone adds a screen without adding it to the rail.
 */

export const APP_TITLE = 'phibian — Admin';

/** href → label, longest href first so `/settings/org` beats `/settings`. */
function routeLabels(): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  // Rail first, hub cards second: ES2019 sort is stable, so on equal-length
  // hrefs the earlier pair wins. A hub FACE therefore titles as its hub
  // ('/compliance' → "Compliance", not its Overview card), while every
  // non-face card contributes its own, more specific name ('/time' →
  // "Time & Timesheets", '/time/approvals' → "Decisions").
  for (const group of NAV_MAP) for (const item of group.items) pairs.push([item.href, item.label]);
  for (const hub of HUBS) for (const card of hub.cards) pairs.push([card.href, card.label]);
  for (const section of SETTINGS_SECTIONS) {
    for (const item of section.items) pairs.push([item.href, item.label]);
  }
  pairs.push(['/settings', 'Settings']);
  pairs.push(['/me/profile', 'My profile']);
  pairs.push(['/help', 'Help']);
  pairs.push(['/preview/worker', 'Worker preview']);
  return pairs.sort((a, b) => b[0].length - a[0].length);
}

const LABELS = routeLabels();

/**
 * The title for a pathname. Matches the longest nav href that is a path prefix
 * of it, so detail routes (`/people/abc123`) inherit their section's name
 * rather than showing an opaque id — the id is on the screen already; the tab
 * needs to say which part of the product this is.
 */
export function pageTitle(pathname: string): string {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (path === '/' || path === '') return `Right now — ${APP_TITLE}`;
  for (const [href, label] of LABELS) {
    if (href === '/') continue;
    if (path === href || path.startsWith(`${href}/`)) return `${label} — ${APP_TITLE}`;
  }
  return APP_TITLE;
}
